import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { getEquipmentDetail, getAllMaintenanceHistory, updateIssueStatus } from './api/mechanicalApi';
import EquipmentInfo from './Components/Equipment/EquipmentInfo';
import HudsonAIHealthScore from './Components/Equipment/HudsonAIHealthScore';
import MaintenanceHistoryChart from './Components/Equipment/MaintenanceHistoryChart';
import IssuesTable from './Components/Equipment/IssuesTable';
import ServiceHistoryTable from './Components/Equipment/ServiceHistoryTable';
import GalleryModal from './Components/Shared/GalleryModal';
import InspectionDetailsModal from './Components/Shared/InspectionDetailsModal';
import PriorityServiceModal from './Components/PriorityServiceModal/PriorityServiceModal';
import Spinner from 'react-bootstrap/Spinner';
import { Alert } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';

function Equipment() {
  const navigate = useNavigate();
  const { equipmentId } = useParams();
  const context = useOutletContext?.() || {};
  const { buildingName, buildingId } = context;
  const [detail, setDetail] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectionData, setInspectionData] = useState(null);
  const [showStatusBanner, setShowStatusBanner] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isUpdatingIssue, setIsUpdatingIssue] = useState(false);

  useEffect(() => {
    if (!equipmentId || !buildingId) {
      console.error('Equipment detail requires both equipmentId and buildingId');
      setIsLoading(false);
      return;
    }
    
    console.log('[Equipment] Fetching equipment detail for', { equipmentId, buildingId });
    
    getEquipmentDetail(equipmentId, buildingId)
      .then(async (data) => {
        console.log('[Equipment] Equipment detail loaded:', {
          id: data.id,
          name: data.name,
          serviceHistoryCount: Array.isArray(data.serviceHistory) ? data.serviceHistory.length : 'N/A',
        });

        setDetail(data);
        document.title = `${data.name} Details`;

        const baseHistory = Array.isArray(data.serviceHistory) ? data.serviceHistory : [];
        setServiceHistory(baseHistory);

        // Fallback: if service history is empty, pull from building-level maintenance history
        if (baseHistory.length === 0) {
          try {
            console.log('[Equipment] No serviceHistory in detail; fetching building-level maintenance history...');
            const allHistory = await getAllMaintenanceHistory(buildingId);
            const filtered = (allHistory || []).filter((visit) => {
              // Support multiple possible equipment ID shapes
              let ids = [];
              if (Array.isArray(visit.equipmentId)) {
                ids = visit.equipmentId;
              } else if (Array.isArray(visit.equipmentIds)) {
                ids = visit.equipmentIds;
              } else if (visit.equipmentId) {
                ids = [visit.equipmentId];
              }

              const cleanTarget = String(equipmentId).replace(/-/g, '');
              return ids.some((id) => String(id).replace(/-/g, '') === cleanTarget);
            });

            console.log('[Equipment] Fallback maintenance history count for equipment:', filtered.length);
            if (filtered.length > 0) {
              setServiceHistory(filtered);
            }
          } catch (error) {
            console.error('[Equipment] Failed to load fallback maintenance history:', error);
          }
        }
      })
      .catch((e) => {
        console.error('Equipment detail fetch failed', e);
      })
      .finally(() => setIsLoading(false));
    
    // Scroll to top when equipment changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [equipmentId, buildingId]);

  function openGallery(images, startIndex) {
    setGalleryImages(images || []);
    setGalleryIndex(startIndex || 0);
    setGalleryOpen(true);
  }
  
  function closeGallery() {
    setGalleryOpen(false);
  }
  
  function prevImage() {
    setGalleryIndex((i) => Math.max(0, i - 1));
  }
  
  function nextImage() {
    setGalleryIndex((i) => Math.min(galleryImages.length - 1, i + 1));
  }

  function openInspectionDetails(data, type) {
    setInspectionData({ ...data, type });
    setShowInspectionModal(true);
  }

  function handleButtonClick(message) {
    setStatusMessage(message);
    setShowStatusBanner(true);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setShowStatusBanner(false), 3000);
  }

  function handleIssueStatusChange(issueId, newStatus) {
    if (!issueId) return;
    setDetail((prev) => {
      if (!prev || !Array.isArray(prev.issues)) return prev;
      return {
        ...prev,
        issues: prev.issues.map((iss) => iss.id === issueId ? { ...iss, status: newStatus } : iss)
      };
    });
  }

  async function handleIssueRowToggle(issue, nextStatus) {
    if (!issue?.id || isUpdatingIssue) return;
    try {
      setIsUpdatingIssue(true);
      await updateIssueStatus(issue.id, nextStatus);
      handleIssueStatusChange(issue.id, nextStatus);
    } catch (err) {
      console.error('Failed to update issue status', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingIssue(false);
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <p>Equipment not found</p>
      </div>
    );
  }

  return (
    <div key={equipmentId}>
      {showStatusBanner && (
        <Alert
          variant="success"
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            margin: 0,
            padding: '0.5rem 1rem',
            width: 'auto',
            minWidth: '300px',
            textAlign: 'center'
          }}
        >
          {statusMessage}
        </Alert>
      )}
      
      <div className="mechanical-equipment-detail" style={{ width: '93.5%', marginLeft: 'auto', marginRight: 'auto', padding: '0.5rem 0 1.5rem 0', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <button
            aria-label="Back to mechanical overview"
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#dbeafe',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#3b82f6'
            }}>
              <FaCog />
            </div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
              {detail.name} Details
            </h1>
          </div>
        </div>

        <div className="mechanical-equipment-summary" style={{
          display: 'flex', 
          gap: '1rem', 
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'stretch'
        }}>
          <EquipmentInfo 
            detail={detail} 
          />
          <MaintenanceHistoryChart 
            maintenanceHistory={serviceHistory || []} 
            showTitle={true}
          />
          <HudsonAIHealthScore 
            detail={detail} 
          />
        </div>
        
        {(detail.issues || []).length > 0 && (
          <IssuesTable 
            issues={detail.issues || []} 
            onOpenGallery={openGallery}
            onOpenInspectionDetails={openInspectionDetails}
            onStatusToggle={handleIssueRowToggle}
          />
        )}
        
        <div style={{ marginTop: '2.5rem' }}>
          <ServiceHistoryTable 
            rows={serviceHistory || []} 
            onOpenGallery={openGallery}
            onOpenInspectionDetails={openInspectionDetails}
            highlightEquipmentId={equipmentId}
          />
        </div>
      </div>

      <GalleryModal
        isOpen={galleryOpen}
        onClose={closeGallery}
        images={galleryImages}
        index={galleryIndex}
        onPrev={prevImage}
        onNext={nextImage}
      />

      <PriorityServiceModal
        show={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        buildingName={buildingName}
        onServiceRequest={handleButtonClick}
      />

      <InspectionDetailsModal
        show={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        data={inspectionData}
        type={inspectionData?.type}
        onButtonClick={handleButtonClick}
        onStatusChange={handleIssueStatusChange}
        buildingName={buildingName}
      />
    </div>
  );
}

export default Equipment;
